/** Display helpers shared by Mini App trade cards (mirrors backend tradeNotifyFormat). */

export function formatPrice(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const a = Math.abs(n);
  if (a >= 1000) return n.toFixed(2);
  if (a >= 10) return n.toFixed(4);
  if (a >= 1) return n.toFixed(5);
  if (a >= 0.1) return n.toFixed(5);
  if (a >= 0.01) return n.toFixed(6);
  return n.toFixed(8);
}

export function formatQty(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const a = Math.abs(n);
  if (a >= 100) return n.toFixed(2);
  if (a >= 1) return n.toFixed(4);
  return n.toFixed(6);
}

export function formatUsd(n: number): string {
  if (!Number.isFinite(n)) return '—';
  const sign = n < 0 ? '−' : '';
  return `${sign}$${Math.abs(n).toFixed(2)}`;
}

export function formatPct(n: number): string {
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return '0.00%';
  const sign = n > 0 ? '+' : '−';
  return `${sign}${Math.abs(n).toFixed(2)}%`;
}

export function formatRr(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '—';
  return n.toFixed(2);
}

export function distancePct(side: string, entry: number, level: number): number {
  if (!(entry > 0) || !Number.isFinite(level)) return NaN;
  const isLong = side.toUpperCase() === 'LONG';
  return isLong ? ((level - entry) / entry) * 100 : ((entry - level) / entry) * 100;
}

export function rewardRisk(entry: number, stopLoss: number, tp: number): number {
  const risk = Math.abs(entry - stopLoss);
  if (risk < 1e-12) return NaN;
  return Math.abs(tp - entry) / risk;
}

export function effectiveStopLoss(side: string, entry: number, sl: number | null | undefined): number | null {
  if (sl == null || !(sl > 0)) return null;
  if (!(entry > 0)) return sl;
  const isLong = side.toUpperCase() === 'LONG';
  if (isLong && sl <= entry * 0.05) return null;
  if (!isLong && sl >= entry * 1.5) return null;
  return sl;
}

export function tpCloseHint(order: number, lastOrder: number, retouchPct: number): string {
  if (order === lastOrder) return '100% last';
  return `${retouchPct}% pullback`;
}

export type TpView = {
  order: number;
  price: number;
  pct: string;
  hint: string;
  rr: string;
};

export type TradeCardMetrics = {
  notional: number;
  margin: number | null;
  sl: { price: number; pct: string; riskUsd: string; balPct: string | null } | null;
  tps: TpView[];
  rrLine: string | null;
};

export function computeTradeCardMetrics(input: {
  side: string;
  entry: number;
  size: number;
  stopLoss: number | null;
  takeProfits: { order: number; price: number }[];
  leverage: number | null;
  balanceUsdt?: number | null;
  retouchClosePct: number;
}): TradeCardMetrics {
  const { side, entry, size, leverage, retouchClosePct } = input;
  const sl = effectiveStopLoss(side, entry, input.stopLoss);
  const notional = entry > 0 && size > 0 ? entry * size : NaN;
  const margin = leverage != null && leverage > 0 && Number.isFinite(notional) ? notional / leverage : null;
  const tpsSorted = [...input.takeProfits].sort((a, b) => a.order - b.order);
  const lastOrder = tpsSorted.length > 0 ? Math.max(...tpsSorted.map((t) => t.order)) : 0;

  let slView: TradeCardMetrics['sl'] = null;
  if (sl != null) {
    const riskUsd = size > 0 ? size * Math.abs(entry - sl) : NaN;
    slView = {
      price: sl,
      pct: formatPct(distancePct(side, entry, sl)),
      riskUsd: Number.isFinite(riskUsd) ? formatUsd(riskUsd) : '—',
      balPct:
        input.balanceUsdt != null && input.balanceUsdt > 0 && Number.isFinite(riskUsd)
          ? `${((riskUsd / input.balanceUsdt) * 100).toFixed(2)}% bal`
          : null,
    };
  }

  const tps: TpView[] = tpsSorted.map((tp) => ({
    order: tp.order,
    price: tp.price,
    pct: formatPct(distancePct(side, entry, tp.price)),
    hint: tpCloseHint(tp.order, lastOrder, retouchClosePct),
    rr: sl != null ? formatRr(rewardRisk(entry, sl, tp.price)) : '—',
  }));

  const rrLine =
    sl != null && tps.length > 0 ? tps.map((t) => `TP${t.order} ${t.rr}`).join('  ·  ') : null;

  return { notional, margin, sl: slView, tps, rrLine };
}
